import { useEffect, useId, useRef } from 'react';
import type { Day } from '../../fixtures/plan';
import dayStyles from './DayDetails.module.css';
import v2Styles from './ClearLogPromptV2.module.css';

/**
 * B309 ruling 1b (founder 2026-10-01) — marking a day NOT done wipes what the
 * athlete logged on it (`usePlanDayCompletion.toggle` clears the effort, and
 * the server clears the distance on a completed to upcoming transition). So
 * when a day has either, the un-complete asks first, inline:
 *
 *   This also clears the effort and distance you logged.   [Keep it] [Clear it]
 *
 * "Clear it" goes on to the existing un-complete write; "Keep it" writes
 * nothing and leaves the day as it was. With nothing logged there is nothing to
 * ask and the un-complete goes straight through, as before. The same prompt
 * serves the day panel's "Mark as not done" and the hero's "Done · Tap to
 * undo", so the two cannot disagree. No new visual design: the two buttons are
 * the day panel's existing ones ("Keep it" the filled one, the safe answer;
 * "Clear it" the neutral one) and the line is the panel's body text.
 */

/**
 * The line to show, or null when the day has nothing logged (no prompt). Says
 * only "the effort" or only "the distance" when only that one is logged. What
 * counts as logged is exactly what the day panel shows: an effort rating, and a
 * distance above zero.
 */
export function clearLogLine(day: Pick<Day, 'rpe' | 'actualDistance'>): string | null {
  const effort = typeof day.rpe === 'number';
  const distance = !!day.actualDistance && day.actualDistance.value > 0;
  if (!effort && !distance) return null;
  const what = effort && distance ? 'the effort and distance' : effort ? 'the effort' : 'the distance';
  return `This also clears ${what} you logged.`;
}

export type ClearLogPromptProps = {
  /** From `clearLogLine`. */
  line: string;
  /** "Clear it": go on to the existing un-complete write. */
  onClear: () => void;
  /** "Keep it" (also Escape): write nothing, leave the day as it was. */
  onKeep: () => void;
  /** Extra class for the wrapper, so the hero can give it its own margins. */
  className?: string;
  /**
   * W1-TODAY-HERO — `'v2'` is the 2.0 look (frame 1g). Same line, same two
   * buttons, same focus and Escape behavior; only the stylesheet differs.
   * Absent = the day panel's look, byte for byte.
   */
  variant?: 'v2';
};

export function ClearLogPrompt({ line, onClear, onKeep, className, variant }: ClearLogPromptProps) {
  const styles = variant === 'v2' ? v2Styles : dayStyles;
  const lineId = useId();
  const keepRef = useRef<HTMLButtonElement>(null);
  // Focus lands on the safe answer, so a stray Enter or Space keeps the log.
  useEffect(() => {
    keepRef.current?.focus();
  }, []);

  return (
    <div
      role="group"
      aria-labelledby={lineId}
      className={[styles.confirm, className].filter(Boolean).join(' ')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onKeep();
        }
      }}
    >
      <p id={lineId} className={styles.body}>
        {line}
      </p>
      <div className={styles.confirmButtons}>
        <button ref={keepRef} type="button" className={styles.action} onClick={onKeep}>
          Keep it
        </button>
        <button type="button" className={styles.undo} onClick={onClear}>
          Clear it
        </button>
      </div>
    </div>
  );
}
